import { AiOutlineQuestionCircle } from "@react-icons/all-files/ai/AiOutlineQuestionCircle";
import { FiPrinter } from "@react-icons/all-files/fi/FiPrinter";
import { Modal, Popover } from "antd";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { clearRetailMatchResults } from "../../../../../Actions/RetailMatchActions";
import { featureFlags } from "../../../../../Common/CurrentFeatureFlags";
import { ReportTypeEnum } from "../../../../../Enums/ReportTypeEnum";
import { getAllReportsLink } from "../../../../../Logic/ReportLogic";
import { IRetailMatchJob } from "../../../../../Models/IRetailMatch";
import { IUser } from "../../../../../Models/IUser";
import { gpsPrintReadySelector } from "../../../../../Reducers/Selectors/GPSInsightsSelectors";
import { userSelector } from "../../../../../Reducers/Selectors/UserSelectors";
import {
    ButtonWrap,
    ReportActionButton, ReportBackLink, ReportHeaderContentContainer,
    ReportHeaderInnerWrap, ReportHeaderWrap, ReportSharingMenu
} from "../../../../Common";
import { HideUpToTabletXtraLarge } from "../../../../Common/DigitalShare/DigitalShareCommonStyles";

interface RetailMatchHeaderProps {
    multiBrand: boolean;
    locationAddress: string;
    job: IRetailMatchJob;
    isSharing?: boolean;
    onPrintClick: () => void;
    onShareClick: () => void;
}

const iconFill = "#515151";

const RetailMatchHeader: FunctionComponent<RetailMatchHeaderProps> = (
    props: RetailMatchHeaderProps
) => {
    const { job, isSharing } = props;
    /** misc */
    const dispatch = useDispatch();
    /** state */
    const [viewVideo, setViewVideo] = useState<boolean>(false);
    const [printReady, setPrintReady] = useState<boolean>(false);
    /** selectors */
    const user: IUser = useSelector(userSelector);
    const printReadyCountdown = useSelector(gpsPrintReadySelector);
    /** effects */

    useEffect(() => {
        viewVideo ? videoPlayStop("play") : videoPlayStop("stop");
    }, [viewVideo]);

    useEffect(() => {
        let timeout = null;

        if (printReadyCountdown && !printReady) {
            timeout = setTimeout(() => {
                setPrintReady(true);
            }, 3000);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [printReadyCountdown]);

    const videoPlayStop = (action: "play" | "stop") => {
        const video = document.getElementById(
            "RetailMatchVideo"
        ) as HTMLVideoElement;
        if (video) {
            switch (action) {
                case "play":
                    video.play();
                    break;
                case "stop":
                default:
                    video.pause();
                    video.currentTime = 0;
                    break;
            }
        }
    };

    const handleViewAll = (): void => {
        dispatch(clearRetailMatchResults());
        window.location.href = getAllReportsLink(user);
    };

    /*
    Master control switch for removing the print button/
    If true, do not render under any circumstance.
    If false, fall back to any other logic controlling its render
    */
    const masterRemovePrintButton = false;

    let renderPrintButton = true;
    if ((props.multiBrand && !featureFlags.DigitalShare.PrintMultiBrand) || masterRemovePrintButton) {
        renderPrintButton = false;
    }

    return (
        <ReportHeaderWrap>
            <ReportHeaderInnerWrap>
                <ReportBackLink
                    text="All Reports"
                    hide={isSharing}
                    onClick={handleViewAll}
                />
                <ReportHeaderContentContainer>
                    <img src="/Images/Match/BuxtonMatch.svg" />
                    <ButtonWrap style={{ width: "450px" }}>
                        <ReportActionButton
                            title={"Learn About Buxton Match"}
                            icon={
                                <AiOutlineQuestionCircle
                                    style={{ marginRight: "8px" }}
                                    fill={iconFill}
                                    size="22px"
                                />
                            }
                            clickCallback={() => {
                                setViewVideo(true);
                            }}
                            style={{ marginRight: "8px" }}
                        />
                        <Modal
                            title=""
                            visible={viewVideo}
                            onCancel={() => {
                                setViewVideo(false);
                            }}
                            footer={null}
                            width={700}
                        >
                            <video
                                id="RetailMatchVideo"
                                width="100%"
                                autoPlay={true}
                                style={{ marginTop: "20px" }}
                            >
                                <source
                                    src="https://cdn.platform.buxtonco.com/videos/platform/scout/buxton-scout-retail-match-v12.mp4"
                                    type="video/mp4"
                                />
                                Your browser does not support the video tag.
                            </video>
                        </Modal>
                        {!isSharing && (
                            <>
                                <ReportSharingMenu
                                    jobId={job.jobId}
                                    title={job.title}
                                    reportType={ReportTypeEnum.RetailMatch}
                                    onShareClick={props.onShareClick}
                                    multiBrandOverview={props.multiBrand}
                                />
                                {renderPrintButton && (
                                    <Popover
                                        content={
                                            printReady ? (
                                                <span>
                                                    PDF reports will open in a new tab or window.
                                                    <br />
                                                    If you have a pop-up blocker installed, you may need
                                                    to add this site to your whitelist/exclusions.
                                                </span>
                                            ) : (
                                                <span>Report loading, please wait</span>
                                            )
                                        }
                                        trigger="hover"
                                        placement="top"
                                    >
                                        <HideUpToTabletXtraLarge>
                                            <ReportActionButton
                                                title={"Print"}
                                                icon={<FiPrinter />}
                                                clickCallback={props.onPrintClick}
                                                style={{ marginRight: "8px" }}
                                                disabled={!printReady}
                                            />
                                        </HideUpToTabletXtraLarge>
                                    </Popover>
                                )}
                            </>
                        )}
                    </ButtonWrap>
                </ReportHeaderContentContainer>
            </ReportHeaderInnerWrap>
        </ReportHeaderWrap>
    );
};

export default RetailMatchHeader;
